import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL } from 'config';

interface Certificate {
  _id: string;
  name: string;
  description: string;
  imageUrl: string;
  fileUrl: string;
  order: number;
  createdAt: string;
}

const Certificates = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Skeleton data for loading state
  const skeletonCertificates = Array(6).fill(null);

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/api/certificates`);
        
        // Sort certificates by order
        const sortedCertificates = response.data.sort((a: Certificate, b: Certificate) => a.order - b.order);
        setCertificates(sortedCertificates);
      } catch (err) {
        console.error('Error fetching certificates:', err);
        // Don't set certificates to empty array on error to maintain skeleton layout
      } finally {
        setIsLoading(false);
      }
    };

    fetchCertificates();
  }, []);

  const handleCertificateClick = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
  };

  const handleCloseModal = () => {
    setSelectedCertificate(null);
  };

  const handleDownload = (certificate: Certificate) => {
    window.open(`${API_URL}${certificate.fileUrl}`, '_blank');
  };

  // Render skeleton loading state
  const renderSkeletonState = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletonCertificates.map((_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="aspect-w-4 aspect-h-3 bg-gray-200 animate-pulse"></div>
          <div className="p-4">
            <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-full animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Render actual content
  const renderActualContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {certificates.map((certificate) => (
        <div
          key={certificate._id}
          className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02]"
        >
          <div 
            onClick={() => handleCertificateClick(certificate)} 
          >
            <img
              src={`${API_URL}${certificate.imageUrl}`}
              alt={certificate.name}
              className="w-full h-auto"
            />
          </div>
          <div className="p-4">
            <h3 className="font-semibold text-primary-dark mb-2">{certificate.name}</h3>
            <p className="text-gray-600 text-sm mb-4">{certificate.description}</p>
            <button 
              onClick={() => handleDownload(certificate)}
              className="w-full bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Скачать сертификат
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Дилерские сертификаты</h1>
      
      {isLoading || certificates.length === 0 ? renderSkeletonState() : renderActualContent()}

      {/* Modal */}
      {selectedCertificate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleCloseModal}
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-lg shadow-xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
              onClick={handleCloseModal}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="max-h-[80vh] overflow-y-auto">
              <img
                src={`${API_URL}${selectedCertificate.imageUrl}`}
                alt={selectedCertificate.name}
                className="w-full h-auto"
              />
              <div className="p-6 border-t">
                <h3 className="font-semibold text-lg text-primary-dark mb-2">{selectedCertificate.name}</h3>
                <p className="text-gray-600 mb-4">{selectedCertificate.description}</p>
                <button 
                  onClick={() => handleDownload(selectedCertificate)}
                  className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Скачать сертификат
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Info Section */}
      <div className="mt-12 bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-4">
          Официальный партнер ведущих производителей
        </h2>
        <p className="text-gray-600 mb-6">
          Все наши сертификаты подтверждают право на продажу и обслуживание оборудования. 
          Мы регулярно проходим обучение и подтверждаем квалификацию наших специалистов.
        </p>
        <div className="flex gap-4">
          <Link 
            to="/callback"
            className="bg-primary text-white px-8 py-3 rounded-md hover:bg-primary-dark transition-colors"
          >
            Связаться с нами
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Certificates; 